import type { Session } from '@supabase/supabase-js';
import type { ForqSupabaseClient } from './client';
import type {
  AccessRequestInput,
  AuthenticatedSession,
  AuthGateway,
  Credentials,
  SessionListener,
} from '@forq/contracts';
import { SupabaseOperationError } from './errors';

export type {
  AccessRequestInput,
  AuthenticatedSession,
  AuthGateway,
  Credentials,
  SessionListener,
} from '@forq/contracts';

function mapSession(session: Session | null): AuthenticatedSession | null {
  if (!session) return null;
  const metadata = session.user.user_metadata;
  const displayName = typeof metadata.display_name === 'string'
    ? metadata.display_name
    : typeof metadata.full_name === 'string'
      ? metadata.full_name
      : null;

  return {
    userId: session.user.id,
    email: session.user.email ?? null,
    displayName,
  };
}

export class SupabaseAuthGateway implements AuthGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async getSession(): Promise<AuthenticatedSession | null> {
    const { data, error } = await this.client.auth.getSession();
    if (error) throw new SupabaseOperationError('auth.getSession', error.message);
    return mapSession(data.session);
  }

  async signIn(credentials: Credentials): Promise<AuthenticatedSession> {
    const { data, error } = await this.client.auth.signInWithPassword(credentials);
    if (error) throw new SupabaseOperationError('auth.signIn', error.message);
    const session = mapSession(data.session);
    if (!session) throw new SupabaseOperationError('auth.signIn', 'No session was returned.');
    return session;
  }

  async signOut(): Promise<void> {
    const { error } = await this.client.auth.signOut();
    if (error) throw new SupabaseOperationError('auth.signOut', error.message);
  }

  /**
   * Meldet, wenn sich ÄNDERT, WER angemeldet ist -- und sonst nichts.
   *
   * Supabase feuert `onAuthStateChange` auch dann, wenn sich gar nichts geändert hat: bei jedem
   * Token-Refresh, und -- der teure Fall -- mit einem erneuten `SIGNED_IN`, sobald das Fenster den
   * Fokus zurückbekommt. Wer das ungefiltert durchreicht, startet FORQ komplett neu, sobald jemand
   * einen Dateidialog öffnet: die Startsequenz läuft wieder, und die halb ausgefüllte Maske ist
   * weg. Genau das ist beim Datenimport passiert -- Datei auswählen, zurückkommen, alles verloren.
   *
   * Der Port heißt `onSessionChange`, nicht `onAuthEvent`: gemeldet wird nur, was die Frage
   * "wer ist angemeldet?" anders beantwortet als vorher.
   */
  onSessionChange(listener: SessionListener): () => void {
    // undefined = noch kein Ereignis gesehen; null = niemand angemeldet.
    let known: string | null | undefined;

    const { data } = this.client.auth.onAuthStateChange((event, session) => {
      const mapped = mapSession(session);
      const userId = mapped?.userId ?? null;

      // Bestandsaufnahme beim Start bzw. reine Token-Erneuerung: nur merken, nicht melden.
      // Den Anfangszustand lädt der Aufrufer selbst.
      if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
        known = userId;
        return;
      }

      if (known !== undefined && userId === known) return;
      known = userId;
      listener(mapped);
    });
    return () => data.subscription.unsubscribe();
  }

  async requestPasswordReset(email: string, redirectTo: string): Promise<void> {
    const { error } = await this.client.auth.resetPasswordForEmail(email.trim().toLowerCase(), { redirectTo });
    // Fehler NICHT durchreichen, wenn die Adresse unbekannt ist: die Antwort darf nicht verraten,
    // welche Konten es gibt. Supabase antwortet hier ohnehin meist neutral -- wir machen es
    // ausdruecklich, damit sich das nicht mit einer Bibliotheksversion aendert.
    if (error && error.status !== 400) {
      throw new SupabaseOperationError('auth.requestPasswordReset', error.message);
    }
  }

  async updatePassword(password: string): Promise<void> {
    const { error } = await this.client.auth.updateUser({ password });
    if (error) throw new SupabaseOperationError('auth.updatePassword', error.message);
  }

  async requestAccess(input: AccessRequestInput): Promise<void> {
    // Die Domainpruefung sitzt im Server (forq.request_access) -- das Formular ist oeffentlich,
    // eine Pruefung im Browser waere nur Dekoration.
    const { error } = await this.client.rpc('request_access', {
      p_email: input.email,
      p_display_name: input.displayName,
      p_note: input.note,
    });
    if (error) throw new SupabaseOperationError('auth.requestAccess', error.message);
  }
}
